// DevAppPreview.js — Preview iframe du build CRA sandbox

import React, { useState, useEffect, useCallback, memo } from 'react';
import { Monitor, RefreshCw, Terminal } from 'lucide-react';
import DevEditorPane, { DevEditorLoading } from './DevEditorPane';
import { getPreviewStatus, getPreviewFrameUrl, DevCockpitApiError } from '../../services/devCockpitApi';
import './devCockpit.css';

function DevAppPreview({ workspaceReady, active }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [iframeKey, setIframeKey] = useState(0);

    const loadStatus = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { preview } = await getPreviewStatus();
            setStatus(preview);
        } catch (e) {
            setStatus(null);
            setError(e instanceof DevCockpitApiError ? e.message : 'Erreur preview');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!active || !workspaceReady) return;
        loadStatus();
    }, [active, workspaceReady, loadStatus]);

    if (!active) return null;

    if (!workspaceReady) {
        return (
            <DevEditorPane>
                <div className="dev-app-preview__empty">
                    <Monitor size={24} />
                    <span>Initialisez le workspace pour activer la preview</span>
                </div>
            </DevEditorPane>
        );
    }

    if (loading && !status) {
        return (
            <DevEditorPane>
                <DevEditorLoading label="Vérification du build…" />
            </DevEditorPane>
        );
    }

    if (error) {
        return (
            <DevEditorPane>
                <div className="dev-app-preview__empty dev-app-preview__empty--error">
                    <span>{error}</span>
                    <button type="button" onClick={loadStatus} className="dev-btn">
                        <RefreshCw size={12} />
                        Réessayer
                    </button>
                </div>
            </DevEditorPane>
        );
    }

    if (!status?.ready) {
        return (
            <DevEditorPane>
                <div className="dev-app-preview__empty">
                    <Terminal size={24} />
                    <strong>Build absent</strong>
                    <span>{status?.message || 'Compilez l\'application dans le terminal'}</span>
                    <code className="dev-app-preview__hint">cd oracle-interface && npm run build</code>
                    <button type="button" onClick={loadStatus} className="dev-btn dev-btn--primary">
                        <RefreshCw size={12} />
                        Vérifier à nouveau
                    </button>
                </div>
            </DevEditorPane>
        );
    }

    return (
        <DevEditorPane>
            <div className="dev-app-preview">
                <div className="dev-app-preview__bar">
                    <span className="dev-app-preview__label">
                        <Monitor size={12} />
                        Build CRA — {status.build_path}
                    </span>
                    <button
                        type="button"
                        onClick={() => { setIframeKey((k) => k + 1); loadStatus(); }}
                        className="dev-btn dev-btn--icon"
                        title="Recharger la preview"
                    >
                        <RefreshCw size={12} />
                    </button>
                </div>
                <iframe
                    key={iframeKey}
                    className="dev-app-preview__iframe"
                    src={getPreviewFrameUrl()}
                    title="Preview application ITRIIA"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                />
            </div>
        </DevEditorPane>
    );
}

export default memo(DevAppPreview);
